import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, skip, takeUntil, tap } from 'rxjs/operators';
import { CellsConfig, DynamicTableCellType, DynamicTableData, DynamicTableRow, LazyTableDataSource } from '@rappi/ui/dynamic-table';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { InputComponent } from '@rappi/ui/input';

import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { AlertsType } from '@rappi/ui/alerts';
import { HasPermissionsService } from '../../../shared/services/has-permissions/has-permissions.service';
import { PurchaseOrderListItem, PurchaseOrderSearchFilters } from '../../models/makers-finances-mgr.models';
import { PO_STATUS, PO_STATUS_LABELS, PO_LABEL_TO_CODE, PoStatus, PO_PERMISSIONS, SOURCE_TYPE, SOURCE_TYPE_LABEL } from '../purchase-order-detail/purchase-order.constants';
import { AppState } from '../../../store/states/app.state';
import { StoreName } from '../../../store/definitions/store.constants';

interface FilterOption { id: string; name: string }

// Source type display name → CSS modifier class for the colored dot badge.
const SOURCE_TYPE_CLASS: Record<string, string> = {
  Trato:         'fh-source--trato',
  Client:        'fh-source--client',
  'Paid by Rappi': 'fh-source--paid-by-rappi',
};

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',
  styleUrls: ['./purchase-orders.component.scss']
})
export class PurchaseOrdersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<any>;
  @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
  @ViewChild('sourceTypeTemplate') sourceTypeTemplate: TemplateRef<any>;

  // Filter selects + search input — cleared on country change so country-scoped dropdowns
  // (company, owner) and the table refetch against the new country.
  @ViewChild('makerSelect') private readonly makerSelect: SelectComponent<FilterOption>;
  @ViewChild('companySelect') private readonly companySelect: SelectComponent<FilterOption>;
  @ViewChild('sourceTypeSelect') private readonly sourceTypeSelect: SelectComponent<FilterOption>;
  @ViewChild('statusSelect') private readonly statusSelect: SelectComponent<FilterOption>;
  @ViewChild('ownerSelect') private readonly ownerSelect: SelectComponent<FilterOption>;
  @ViewChild('searchInput') private readonly searchInput: InputComponent;

  tableData: DynamicTableData;

  // Filter selections (kept as the raw option arrays from one-select)
  filterCompanies: FilterOption[] = [];
  filterSourceTypes: FilterOption[] = [];
  filterStatuses: FilterOption[] = [];
  filterMakers: FilterOption[] = [];
  filterOwners: FilterOption[] = [];
  filterIsTest = false;
  searchTerm = '';

  readonly STATUS_LABELS = PO_STATUS_LABELS;
  readonly LABEL_TO_CODE = PO_LABEL_TO_CODE;

  readonly selectConfig = {
    search: true,
    includeAll: true,
    showTotalsChip: false,
    maxNumberOfChips: 1,
    panelClass: 'one-select'
  };

  readonly selectConfigNoSearch = {
    ...this.selectConfig,
    search: false
  };

  companyDataSource = new LazyScrollableDataSource(this.fetchCompanyOptions.bind(this));
  sourceTypeDataSource = new LazyScrollableDataSource(this.fetchSourceTypeOptions.bind(this));
  statusDataSource = new LazyScrollableDataSource(this.fetchStatusOptions.bind(this));
  makerDataSource = new LazyScrollableDataSource(this.fetchMakerOptions.bind(this));
  ownerDataSource = new LazyScrollableDataSource(this.fetchOwnerOptions.bind(this));

  private readonly PAGE_SIZE = 10;
  private readonly PAGE_SIZE_OPTIONS = [10, 20, 30];
  private readonly searchSubject = new Subject<string>();
  private readonly sourceTypeNameById = new Map<number, string>();
  private readonly destroy$ = new Subject<void>();
  // Guards resetTable() while we programmatically clearSelection() on the filters during a
  // country change — each clearSelection() emits selectionChanged → onXxxChange([]) → resetTable(),
  // but we only want one refetch at the end.
  private isRefreshing = false;

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alertsService: AlertsService,
    private readonly permissionsService: HasPermissionsService,
    private readonly store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    // Preload source type catalog so the Source type filter and the column badge have names.
    this.loadSourceTypes();

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe(() => this.resetTable());

    // Refresh filters + table when the country changes — company/owner dropdowns and the PO
    // search are country-scoped. skip(1) ignores the initial value (the table's first load
    // already covers it), matching the pattern in permissions/.../table components.
    this.store.pipe(
      select(StoreName.country),
      skip(1),
      takeUntil(this.destroy$),
    ).subscribe(() => this.onCountryChange());
  }

  // Preload the source type catalog into sourceTypeNameById (used by the Source type filter
  // and the column badge). Clears first so a re-load on country change drops stale names.
  private loadSourceTypes(): void {
    this.financesService.getSourceTypes().subscribe(res => {
      this.sourceTypeNameById.clear();
      res.data.records.forEach(r => this.sourceTypeNameById.set(r.id, r.name));
    });
  }

  ngAfterViewInit(): void {
    this.tableData = this.buildTableConfig();
    this.cdr.detectChanges();
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value.trim();
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  onCompanyChange(values: FilterOption[]): void {
    this.filterCompanies = values ?? [];
    this.resetTable();
  }

  onSourceTypeChange(values: FilterOption[]): void {
    this.filterSourceTypes = values ?? [];
    this.resetTable();
  }

  onStatusChange(values: FilterOption[]): void {
    this.filterStatuses = values ?? [];
    this.resetTable();
  }

  onMakerChange(values: FilterOption[]): void {
    this.filterMakers = values ?? [];
    this.resetTable();
  }

  onOwnerChange(values: FilterOption[]): void {
    this.filterOwners = values ?? [];
    this.resetTable();
  }

  // Draft POs are editable, so the row action opens the edit view directly (with ?from=list so the
  // edit Cancel returns to this list). Non-draft POs open the read-only detail view.
  viewOrder(po: PurchaseOrderListItem, statusCode: string | null): void {
    if (statusCode === PO_STATUS.DRAFT) {
      this.router.navigate(['/financial-hub/purchase-orders', po.id, 'edit'], { queryParams: { from: 'list' } });
    } else {
      this.router.navigate(['/financial-hub/purchase-orders', po.id]);
    }
  }

  // business_approver can only open Paid by Rappi POs; admins and all other roles can open any PO.
  // (isAdmin is checked explicitly because checkPermission returns true for admins on every slug.)
  canViewDetail(po: PurchaseOrderListItem): boolean {
    const isAdmin = this.permissionsService.isAdmin();
    const isBusinessApprover = this.permissionsService.checkPermission(PO_PERMISSIONS.BUSINESS_APPROVER);
    if (isAdmin || !isBusinessApprover) {
      return true;
    }
    return po.source_type === SOURCE_TYPE_LABEL[SOURCE_TYPE.PAID_BY_RAPPI];
  }

  formatBudget(amount: number | null, currency: string | null): string {
    if (amount == null) {
      return '';
    }
    const locale = currency === 'BRL' ? 'pt-BR' : currency === 'MXN' ? 'es-MX' : 'es-CO';
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  onTestToggle(): void {
    this.resetTable();
  }

  get isAdmin(): boolean {
    return this.permissionsService.isAdmin();
  }

  private resetTable(): void {
    if (this.isRefreshing) {
      return;
    }
    (this.tableData.dataSource as LazyTableDataSource<DynamicTableRow>).resetTable();
  }

  // Country change: clear every filter + the search box, then refetch the table once.
  private onCountryChange(): void {
    this.isRefreshing = true;

    // Clear the search box (DOM + state) without emitting a debounced resetTable.
    this.searchTerm = '';
    this.searchInput.control.setValue('');

    // clearSelection() emits selectionChanged → onXxxChange([]) → resetTable(); the guard
    // suppresses those intermediate resets so we refetch exactly once at the end.
    this.makerSelect?.clearSelection();
    this.companySelect?.clearSelection();
    this.sourceTypeSelect?.clearSelection();
    this.statusSelect?.clearSelection();
    this.ownerSelect?.clearSelection();

    // Reset local filter state in case any select had no ViewChild yet (defensive).
    this.filterMakers = [];
    this.filterCompanies = [];
    this.filterSourceTypes = [];
    this.filterStatuses = [];
    this.filterOwners = [];

    // Refetch every filter's first page against the new country. setFilterValue('') clears
    // the cache (resetData) AND triggers _fetchPage(initPage), which makes the network call.
    // resetData() alone only clears the cache — the dropdown wouldn't refetch until opened.
    this.makerDataSource.setFilterValue('');
    this.companyDataSource.setFilterValue('');
    this.sourceTypeDataSource.setFilterValue('');
    this.statusDataSource.setFilterValue('');
    this.ownerDataSource.setFilterValue('');

    // Re-preload source types so the column badge + Source type filter show the new catalog.
    this.loadSourceTypes();

    this.isRefreshing = false;
    this.resetTable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildFilters(): PurchaseOrderSearchFilters {
    const filters: PurchaseOrderSearchFilters = {
      is_test: this.filterIsTest,
    };
    if (this.searchTerm) {
      filters.q = this.searchTerm;
    }
    if (this.filterMakers.length)     {
      filters.maker_ids       = this.filterMakers.map(m => m.id);
    }
    if (this.filterCompanies.length)  {
      filters.company_ids     = this.filterCompanies.map(c => c.id);
    }
    if (this.filterSourceTypes.length){
      filters.source_type_ids = this.filterSourceTypes.map(s => Number(s.id));
    }
    if (this.filterStatuses.length)   {
      filters.statuses       = this.filterStatuses.map(s => s.id as PoStatus);
    }
    if (this.filterOwners.length)     {
      filters.owner_emails   = this.filterOwners.map(o => o.id);
    }
    return filters;
  }

  private buildTableConfig(): DynamicTableData {
    const dataSource = new LazyTableDataSource<DynamicTableRow>(
      this.fetchOrders.bind(this),
      this.PAGE_SIZE,
      'poId',
      1
    );

    const columns = ['name', 'poId', 'sourceType', 'makerToExecute', 'companyToBill', 'budget', 'currency', 'status', 'actions'];

    const cellsConfig: CellsConfig = {
      name: {
        type: DynamicTableCellType.text,
        title: 'PO Name'
      },
      poId: {
        type: DynamicTableCellType.text,
        title: 'PO ID'
      },
      sourceType: {
        type: DynamicTableCellType.templateRef,
        title: 'Source type'
      },
      makerToExecute: {
        type: DynamicTableCellType.text,
        title: 'Maker to execute'
      },
      companyToBill: {
        type: DynamicTableCellType.text,
        title: 'Company to bill'
      },
      budget: {
        type: DynamicTableCellType.text,
        title: 'PO Budget'
      },
      currency: {
        type: DynamicTableCellType.text,
        title: 'Currency'
      },
      status: {
        type: DynamicTableCellType.templateRef,
        title: 'Status'
      },
      actions: {
        type: DynamicTableCellType.templateRef,
        title: 'Actions'
      }
    };

    return {
      dataSource,
      hideSearch: true,
      pagination: true,
      lazyLoaded: true,
      message: { title: 'No purchase orders found' },
      paginatorOpts: { pageSize: this.PAGE_SIZE, pageSizeOptions: this.PAGE_SIZE_OPTIONS },
      columns,
      cellsConfig
    };
  }

  private fetchOrders(page: number, limit: number): Observable<DynamicTableRow[]> {
    return this.financesService.searchPurchaseOrders(this.buildFilters(), page, limit).pipe(
      tap(res => {
        if (this.tableData?.paginator) {
          this.tableData.paginator.length = res.data.pagination.total_record;
        }
      }),
      map(res => res.data.records.map(po => this.toRow(po))),
      catchError(() => {
        this.alertsService.openAlerts('Could not load purchase orders. Please try again.', AlertsType.error);
        return of([]);
      }),
    );
  }

  private toRow(po: PurchaseOrderListItem): DynamicTableRow {
    const sourceTypeName = po.source_type ?? '';
    const statusCode = this.LABEL_TO_CODE[po.status];
    return {
      name:          { value: po.name },
      poId:          { value: po.po_id },
      sourceType:    { templateRef: this.sourceTypeTemplate, context: { sourceType: sourceTypeName, colorClass: SOURCE_TYPE_CLASS[sourceTypeName] } },
      makerToExecute:{ value: po.maker_to_execute ?? '' },
      companyToBill: { value: po.company_to_bill ?? '' },
      budget:        { value: this.formatBudget(po.budget, po.currency) },
      currency:      { value: po.currency ?? '' },
      status:        { templateRef: this.statusTemplate, context: { label: po.status, code: statusCode } },
      actions:       { templateRef: this.actionsTemplate, context: { po, statusCode } },
    };
  }

  private fetchCompanyOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return this.financesService.searchCompanies(this.storeAux.country, search || undefined).pipe(
      map(res => res.data.map(c => ({ id: c.id, name: c.name }))),
      catchError(() => of([])),
    );
  }

  private fetchSourceTypeOptions(): Observable<FilterOption[]> {
    return this.financesService.getSourceTypes().pipe(
      map(res => res.data.records.map(r => ({ id: String(r.id), name: r.name }))),
      catchError(() => of([])),
    );
  }

  private fetchStatusOptions(page = 0): Observable<FilterOption[]> {
    // Static catalog — return all items on the first page and nothing afterwards so
    // LazyScrollableDataSource doesn't concatenate duplicates on scroll.
    if (page > 0) {
      return of([]);
    }
    return of(
      (Object.entries(PO_STATUS_LABELS) as [PoStatus, string][]).map(([id, name]) => ({ id, name }))
    );
  }

  private fetchMakerOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return this.financesService.searchMakers(page, limit, search).pipe(
      map(res => res.content.map(m => ({ id: m.code || String(m.id), name: m.name }))),
      catchError(() => of([])),
    );
  }

  private fetchOwnerOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return this.financesService.searchOwners(this.storeAux.country, page + 1, limit, search || undefined).pipe(
      map(res => res.data.records.map(o => ({ id: o.email, name: o.email }))),
      catchError(() => of([])),
    );
  }
}
